const products = [
  {
    name: "MDM Calc: E/M Optimizer",
    slug: "mdm-calc",
    imageUrl: "/images/products/mdm-calc.png",
    tagline: "Unified MDM optimization for primary care and specialty workflows.",
    description: "A modern, unified E/M coding app that supports primary care, specialty, outpatient, consult, and surgery-clinic modifier workflows. Includes \"Why Not Higher?\" guidance and practical paths to level up documentation quality.",
    guidanceLastUpdated: "February 2026",
    guidanceSummary: "Aligned to the 2023 E/M framework with applicable 2025 updates.",
    paragraphs: [
      "MDM Calc combines the strengths of E/M Assist: Primary Care and E/M Assist: Specialty into one streamlined workflow. It supports office and consult coding across primary care and specialty settings, with profile-aware visit setup and optional surgery-clinic modifier support where appropriate.",
      "The app emphasizes decision clarity, not just code output. In addition to core CPT suggestions, MDM Calc provides explanation-first tooling with \"Why this result?\" and premium \"Why Not Higher?\" upgrade-path guidance so clinicians can quickly understand what documentation supports a higher level.",
      "MDM Calc is designed for fast, repeatable use in real clinical workflows. A 30-day premium trial is included from first open, then a one-time unlock is available for advanced optimization features."
    ],
    links: [
      {
        label: "Get on the App Store",
        url: "https://apps.apple.com/app/id6759225468",
      }
    ],
    hasDetailPage: true,
    comingSoon: false
  },
  {
    name: "CardioForge",
    slug: "cardioforge",
    imageUrl: "/images/products/cardioforge.png",
    tagline: "Heart rate tracking for indoor fitness classes and training.",
    description: "Connect your Bluetooth heart rate monitor and track your performance across 60+ workout types. Real-time heart rate zones, calorie tracking, and workout history—all designed for indoor fitness enthusiasts. Optional Apple Health sync with premium features.",
    paragraphs: [
      "CardioForge is built for people who train hard in gyms, studios, and home workouts. Whether you're grinding through BJJ rounds, crushing a CrossFit WOD, or pushing through indoor cycling classes, CardioForge gives you the metrics that matter: real-time heart rate, intensity zones, and accurate calorie burn across more than 60 workout types from martial arts to cardio machines.",
      "Connect any Bluetooth heart rate monitor and you're ready to go. The app tracks your performance in real-time with clear zone indicators, pause/resume support for interval training, and workout recovery if you need to step away mid-session. Live Activities integration puts your current heart rate right on your lock screen and Dynamic Island, so you can stay focused on your workout instead of your phone.",
      "Your workout data stays local on your device by default—no cloud accounts required. For those who want deeper integration, premium features include Apple Health sync to combine your heart rate data with Apple's fitness ecosystem, unlimited workout history, and TCX export for sharing with other platforms. It's fitness tracking that respects your privacy while giving you the insights you need to train smarter."
    ],
    links: [
      {
        label: "Get on the App Store",
        url: "https://apps.apple.com/app/id6752625670",
      }
    ],
    hasDetailPage: true,
    legacy: false
  },
  {
    name: "Reflexo: Speed Training",
    slug: "reflexo",
    imageUrl: "/images/products/reflexo.jpg",
    tagline: "Fast, fun reflex training for anywhere, anytime.",
    description: "Quick 30-second reflex sessions perfect for waiting rooms, commutes, or short breaks. Tap matching colors across five difficulty levels with Game Center leaderboards. No ads, no tracking—just pure, satisfying gameplay.",
    paragraphs: [
      "Reflexo transforms those inevitable spare moments—waiting for appointments, riding the bus, taking work breaks—into something genuinely fun. Each round takes just 30-60 seconds, making it perfect for busy lives where you never know when you'll have a free minute.",
      "The gameplay is beautifully simple: tap the tile that matches the target color as quickly and accurately as possible. Five difficulty levels scale from relaxed to ultra-fast, so you can choose your challenge level whether you're winding down or wanting to push your reflexes to their limits. Game Center leaderboards add just enough friendly competition without overwhelming complexity.",
      "What makes Reflexo special is what it doesn't do. There are no ads interrupting your flow, no subscriptions nagging you, and no tracking systems monitoring your play. It's a one-time purchase that delivers clean, colorful gameplay with satisfying streak bonuses and crisp sound effects—premium mobile gaming the way it should be."
    ],
    links: [
      {
        label: "Get on the App Store",
        url: "https://apps.apple.com/app/id6748332970",
      }
    ],
    hasDetailPage: true,
    legacy: false
  },
  {
    name: "E/M Assist: Primary Care",
    slug: "em-assist-primary-care",
    imageUrl: "/images/products/emassist-primary-care.jpg",
    tagline: "Comprehensive CPT coding for primary care workflows.",
    description: "Legacy app replaced by MDM Calc. Existing users can continue to use this version, but new updates and features are moving to MDM Calc.",
    guidanceLastUpdated: "February 2025",
    guidanceSummary: "Aligned to the 2023 E/M framework with applicable 2025 updates.",
    paragraphs: [
      "E/M Assist: Primary Care expands beyond specialty-focused tools to support the full complexity of primary care practice. From routine office visits to preventive medicine, annual wellness visits, and care management services, it provides CPT code calculations based on Medical Decision Making and time while optimizing for primary care-specific revenue opportunities like G2211 add-on codes.",
      "The app includes age-based preventive visit selection, same-day preventive plus problem visit handling with modifier -25 guidance, and smart G2211 eligibility detection for longitudinal care relationships. Advanced Primary Care Management (APCM) workflow integration helps identify care coordination opportunities, while the offline-first design ensures patient privacy with no data storage or cloud dependencies.",
      "Built specifically for family medicine, internal medicine, and primary care nurse practitioners and physician assistants, the app recognizes the unique workflow patterns of comprehensive primary care. Premium features include G2211 usage analytics, care management opportunity identification, and preventive care calendar tools—all while maintaining complete patient privacy and working entirely offline after download."
    ],
    links: [
      {
        label: "Get on the App Store",
        url: "https://apps.apple.com/app/id6751079357",
      },
      {
        label: "See MDM Calc (Unified Successor)",
        url: "/products/mdm-calc",
      }
    ],
    hasDetailPage: true,
    legacy: true,
    replacementSlug: "mdm-calc",
    replacementName: "MDM Calc: E/M Optimizer"
  },
  {
    name: "E/M Assist: Specialty",
    slug: "em-assist-specialty",
    imageUrl: "/images/products/emassist-specialty.jpg",
    tagline: "Fast, offline CPT coding with zero patient data storage.",
    description: "Legacy app replaced by MDM Calc. Existing users can continue to use this version, but new updates and features are moving to MDM Calc.",
    guidanceLastUpdated: "February 2025",
    guidanceSummary: "Aligned to the 2023 E/M framework with applicable 2025 updates.",
    paragraphs: [
      "E/M Assist was built for specialty providers who need reliable CPT coding assistance without compromising patient privacy. The app calculates accurate codes using Medical Decision Making criteria and time-based factors, but here's what makes it different: it never stores patient names, identifiers, or any personal information—only the anonymous clinical parameters needed for proper coding.",
      "The interface is designed for real clinical workflows. A fast, tap-based system captures problem complexity, data reviewed, and risk factors during or after patient encounters. Everything works completely offline once downloaded, meaning no data transmission, no cloud dependencies, and no external connections that could compromise security. Your coding decisions and the reasoning behind them stay entirely on your device.",
      "For practices that want to track patterns over time, the premium version includes visit history—but even this stores only anonymized coding parameters and timestamps. It's clinical decision support that respects both your need for accurate billing and your patients' right to privacy, designed specifically for the unique challenges facing outpatient specialty practices."
    ],
    links: [
      {
        label: "Get on the App Store",
        url: "https://apps.apple.com/app/id6747998242",
      },
      {
        label: "See MDM Calc (Unified Successor)",
        url: "/products/mdm-calc",
      }
    ],
    hasDetailPage: true,
    legacy: true,
    replacementSlug: "mdm-calc",
    replacementName: "MDM Calc: E/M Optimizer"
  },
]

export default products
